/* ---------------------------------------------------------------------------
 * decorative-video-aria.js
 * ---------------------------------------------------------------------------
 * Removes the redundant aria-label from <video> elements that are hidden from
 * assistive technology.
 *
 * Prestige's video_tag filter auto-generates an aria-label from the media's
 * alt text. On decorative, muted, looping clips the wrapper (or the video
 * itself) also carries aria-hidden="true". Those two signals contradict each
 * other: a screen reader will not announce a hidden element, so the label is
 * dead weight and trips the "hidden element has an accessible name" check.
 *
 * SAFETY RULE: we only remove aria-label when the video is *already* hidden
 * from AT (self or any ancestor aria-hidden="true"). A video that is exposed
 * to AT keeps its label untouched — that label is its text alternative.
 *
 * This does NOT remove loop, does NOT change aria-hidden, and does NOT touch
 * images, 3D models, or video controls. It is purely a markup-hygiene pass.
 * ------------------------------------------------------------------------- */
(function () {
  'use strict';

  // Strip the label only if the video is effectively hidden from AT.
  function cleanVideo(video) {
    if (!video || video.tagName !== 'VIDEO') return;
    if (!video.hasAttribute('aria-label')) return;

    // closest() checks the element itself and all ancestors.
    if (video.closest('[aria-hidden="true"]')) {
      video.removeAttribute('aria-label');
    }
  }

  // Re-evaluate every labelled video within a given root (defaults to document).
  function sweep(root) {
    var scope = (root && root.querySelectorAll) ? root : document;
    var videos = scope.querySelectorAll('video[aria-label]');
    for (var i = 0; i < videos.length; i++) {
      cleanVideo(videos[i]);
    }
  }

  function init() {
    sweep(document);

    // Prestige renders media through <video-media> custom elements and swaps
    // it on variant change, quick view, and cart-drawer events. A toggle of
    // aria-hidden on a wrapper can change whether an inner video is exposed,
    // and the video_tag label may be re-rendered, so we watch for both.
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];

        if (m.type === 'attributes') {
          if (m.attributeName === 'aria-hidden') {
            // Hidden state changed on a video or a wrapper — re-check inside.
            if (m.target.tagName === 'VIDEO') {
              cleanVideo(m.target);
            } else {
              sweep(m.target);
            }
          } else if (m.attributeName === 'aria-label' &&
                     m.target.tagName === 'VIDEO') {
            // Label was (re)added on a re-render — clean it if now hidden.
            cleanVideo(m.target);
          }
          continue;
        }

        // New media injected into the DOM.
        if (m.addedNodes) {
          for (var j = 0; j < m.addedNodes.length; j++) {
            var node = m.addedNodes[j];
            if (node.nodeType !== 1) continue; // elements only
            if (node.tagName === 'VIDEO') {
              cleanVideo(node);
            } else if (node.querySelectorAll) {
              sweep(node);
            }
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'aria-label']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();